import { useQueries } from "@tanstack/react-query";
import { metricsApi } from "../api/metrics";
import { qk } from "../../lib/queryKeys";
import type { MetricsTrendsParams } from "../api/types";
import type { ComparisonGroup } from "./useComparisonGroups";

export interface ComparisonTrendResult {
  group: ComparisonGroup;
  data: ReturnType<typeof metricsApi.getTrends> extends Promise<infer T>
    ? T | undefined
    : undefined;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Fetches the trend series for each comparison group in parallel using the
 * existing trends endpoint (one request per group). Used to overlay one line
 * per group on a single chart. Reuses the same query keys as useMetricsTrends
 * so requests dedupe/cache with the rest of the app.
 */
export const useComparisonTrends = (
  groups: ComparisonGroup[],
  metrics: string[],
  granularity: MetricsTrendsParams["granularity"]
) => {
  const results = useQueries({
    queries: groups.map((group) => {
      const params: MetricsTrendsParams = {
        ...group.filterParams,
        metrics,
        granularity,
      };
      return {
        queryKey: qk.metrics.trends(params),
        queryFn: () => metricsApi.getTrends(params),
        staleTime: 0,
        enabled:
          !!params.user_ids?.length || params.client_id !== undefined,
      };
    }),
  });

  return groups.map((group, index) => ({
    group,
    data: results[index]?.data,
    isLoading: results[index]?.isLoading ?? false,
    isError: results[index]?.isError ?? false,
  }));
};
